import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Container, 
  Alert,
  AlertTitle,
  InputAdornment,
  IconButton,
  Link,
  Grid
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Email, Close } from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Registration Component
 * Provides a form for new users to create an account
 */
const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [department, setDepartment] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { register, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    
    // Validate form
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return;
    }
    
    try {
      const response = await register(name, email, password, department, jobTitle);
      
      console.log('[Register] Registration response:', response);
      console.log('[Register] Account status:', response.data?.accountStatus);
      
      // Check if account is pending approval
      if (response.data?.accountStatus === 'pending') {
        console.log('[Register] Account is PENDING - showing success message');
        setSuccessMessage(
          response.message || 
          'Registration successful! Your account is pending approval. You will receive an email once your account has been reviewed and approved.'
        );
        // Clear form
        setName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setDepartment('');
        setJobTitle('');
      } else {
        console.log('[Register] Account NOT pending - redirecting to dashboard');
        console.log('[Register] Response data:', response.data);
        // Account approved immediately, redirect to dashboard
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('[Register] Registration error:', err);
      setFormError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          mt: 8
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              color: '#003366', 
              fontWeight: 700,
              mb: 1
            }}
          >
            E-Unify
          </Typography>
          <Typography variant="h5" component="h2">
            Create an Account
          </Typography>
        </Box>

        {formError && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {formError}
          </Alert>
        )}
        
        {successMessage && (
          <Alert 
            severity="success" 
            icon={<CheckCircle fontSize="large" />}
            onClose={() => setSuccessMessage('')}
            sx={{ 
              width: '100%', 
              mb: 3,
              p: 3,
              fontSize: '1.1rem',
              border: '2px solid #28a745'
            }}
          >
            <AlertTitle sx={{ fontSize: '1.4rem', fontWeight: 'bold', mb: 2 }}>
              ✅ Registration Successful!
            </AlertTitle>
            
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
              Your account has been created and is <strong>pending approval</strong>.
            </Typography>
            
            <Box sx={{ 
              bgcolor: '#e8f5e9', 
              p: 2, 
              borderRadius: 1, 
              mb: 2,
              border: '1px solid #4caf50'
            }}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Email sx={{ mr: 1, color: '#2e7d32' }} />
                <strong>Email Notification:</strong>
              </Typography>
              <Typography variant="body2" sx={{ ml: 4 }}>
                You will receive an email at <strong>{email}</strong> once your account is approved.
              </Typography>
            </Box>
            
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              📋 <strong>What happens next:</strong>
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 3, mb: 2 }}>
              <li><Typography variant="body2">Our admin team will review your registration</Typography></li>
              <li><Typography variant="body2">You'll receive an approval email (usually within 24-48 hours)</Typography></li>
              <li><Typography variant="body2">Once approved, you can log in and access the platform</Typography></li>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button 
                variant="contained" 
                onClick={() => navigate('/login')}
                sx={{ flex: 1 }}
              >
                Go to Login Page
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => {
                  setSuccessMessage('');
                  setEmail('');
                }}
                sx={{ flex: 1 }}
              >
                Register Another User
              </Button>
            </Box>
          </Alert>
        )}

        {!successMessage && (
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoComplete="name"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                inputProps={{
                  'aria-label': 'Full Name',
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{
                  'aria-label': 'Email Address',
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText="Password must be at least 6 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  'aria-label': 'Password',
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  'aria-label': 'Confirm Password',
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="department"
                label="Department"
                name="department"
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                inputProps={{
                  'aria-label': 'Department',
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="jobTitle"
                label="Job Title"
                name="jobTitle"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
                inputProps={{
                  'aria-label': 'Job Title',
                }}
              />
            </Grid>
          </Grid>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{ mt: 3, mb: 2 }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </Button>
          
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Link component={RouterLink} to="/login" variant="body2">
                {"Already have an account? Sign in"}
              </Link>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Register;
